// Internal PersonaOS URL names. Never use the legacy brand slug here.
export const BRAND_URL_RESERVED_NAMES = new Set(["new", "settings", "admin"]);
export const UUID_URL_TOKEN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function normalizeBrandUrlName(value: string): string {
  return value.normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export function brandUrlNameError(value: string): string | null {
  if (value.length < 2 || value.length > 60) return "Use 2 to 60 characters";
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value)) return "Use lowercase letters, numbers, and single hyphens";
  if (BRAND_URL_RESERVED_NAMES.has(value)) return "That URL name is reserved";
  if (UUID_URL_TOKEN.test(value)) return "A UUID cannot be used as a URL name";
  return null;
}

export function allocateBrandUrlName(name: string, occupied: ReadonlySet<string>): string {
  let base = normalizeBrandUrlName(name).slice(0, 60).replace(/-+$/g, "");
  if (base.length < 2 || UUID_URL_TOKEN.test(base)) base = "brand";
  for (let suffix = 1; ; suffix++) {
    const tail = suffix === 1 ? "" : `-${suffix}`;
    const candidate = base.slice(0, 60 - tail.length).replace(/-+$/g, "") + tail;
    if (!brandUrlNameError(candidate) && !occupied.has(candidate.toLowerCase())) return candidate;
  }
}

// Readable internal URLs are standard for every active authenticated account.
export function brandUrlNamesEligible(
  user: { id?: string; isMaster?: boolean; deletedAt?: unknown } | null | undefined,
): boolean {
  return Boolean(user?.id && !user.deletedAt);
}

// Editing remains under existing PersonaOS admin authorization.
export function canEditBrandUrlName(
  user: { isMaster?: boolean; deletedAt?: unknown } | null | undefined,
): boolean {
  return Boolean(user?.isMaster && !user.deletedAt);
}

export interface BrandUrlIdentity { id: string; urlName?: string | null }

export function brandPath(
  brand: BrandUrlIdentity,
  suffix = "",
  recipient?: { eligible: boolean },
): string {
  const token = recipient?.eligible && brand.urlName && !brandUrlNameError(brand.urlName)
    ? brand.urlName : brand.id;
  return `/brands/${encodeURIComponent(token)}${suffix && !/^[/?#]/.test(suffix) ? "/" : ""}${suffix}`;
}

export function projectBrandUrl(path: string, brands: readonly BrandUrlIdentity[], outbound: boolean): string {
  const match = path.match(/^\/brands\/([^/?#]+)(.*)$/);
  if (!match || BRAND_URL_RESERVED_NAMES.has(match[1])) return path;
  const brand = brands.find(b => outbound ? b.id === match[1] : b.urlName === match[1].toLowerCase());
  if (!brand) return path;
  return outbound ? brandPath(brand, match[2], { eligible: true }) : brandPath(brand, match[2]);
}
